import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { KeyValues, Notice, StatusPill } from "../../../design/components";
import { useMe } from "../../../api/queries";
import { useRoutingParticipants, type ParticipantRow } from "../../../api/routing";
import { dateTime } from "../../../lib/format";
import type { Entity } from "../../../api/types";
import { CopyButton } from "../CopyButton";
import styles from "../CompanyDetail.module.css";

const SCHEME_NOTE = "Peppol 0248, Oman commercial registration";

function matches(row: ParticipantRow, q: string): boolean {
  if (!q) return true;
  return `${row.legalName} ${row.entityCode} ${row.crNumber} ${row.participantId}`
    .toLowerCase()
    .includes(q);
}

export function ParticipantTab({ entity }: { entity: Entity }) {
  const { t } = useTranslation();
  const me = useMe();
  // Ruling 79: the full group directory is a group-admin surface.
  const isGroupAdmin = me.data?.role === "group_admin";
  const participants = useRoutingParticipants("", isGroupAdmin);
  const [query, setQuery] = useState("");

  const directory = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (participants.data ?? []).filter((row) => row.entityId !== entity.id && matches(row, q));
  }, [participants.data, entity.id, query]);

  return (
    <div className={styles.overview}>
      <section className="card">
        <div className="card-head">
          <h2 className="grow">{t("companies.participant.heading", "Peppol participant record")}</h2>
        </div>
        <div className="card-body">
          <KeyValues
            pairs={[
              [
                t("companies.participant.fields.participantId", "Participant ID"),
                <span className="row-tight">
                  <span className="mono">{entity.participantId}</span>
                  <CopyButton
                    value={entity.participantId}
                    label={t("companies.participant.copyParticipantId", "Copy participant ID")}
                  />
                </span>,
              ],
              [t("companies.participant.fields.scheme", "Scheme"), t("companies.participant.schemeNote", SCHEME_NOTE)],
              [
                t("companies.participant.fields.crNumber", "CR number"),
                <span className="row-tight">
                  <span className="mono">{entity.crNumber}</span>
                  <CopyButton
                    value={entity.crNumber}
                    label={t("companies.participant.copyCrNumber", "Copy CR number")}
                  />
                </span>,
              ],
              [
                t("companies.participant.fields.vatin", "VAT identification number"),
                <span className="mono">{entity.vatin ?? "—"}</span>,
              ],
              [
                t("companies.participant.fields.legalName", "Registered legal name"),
                <span className="row-tight">
                  {entity.legalName}
                  <CopyButton
                    value={entity.legalName}
                    label={t("companies.participant.copyLegalName", "Copy registered legal name")}
                  />
                </span>,
              ],
            ]}
          />
          <p className={styles.note}>
            {t(
              "companies.participant.routingNote",
              "Inbound documents route by participant ID to the commercial registration number, never by VAT identification number.",
            )}
          </p>
        </div>
      </section>

      {isGroupAdmin ? (
        <section className="card card-tight">
          <div className="card-head wrap">
            <h2 className="grow">{t("companies.participant.otherCompaniesHeading", "Other companies in the group")}</h2>
            <input
              className="input search"
              type="text"
              aria-label={t("companies.participant.searchAriaLabel", "Search the participant directory")}
              placeholder={t("companies.participant.searchPlaceholder", "Search by name, CR or participant ID")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {participants.isError ? (
            <div className="card-body">
              <Notice kind="fail">
                {t("companies.participant.directoryLoadError", "The participant directory could not be loaded.")}
              </Notice>
            </div>
          ) : directory.length === 0 ? (
            <div className="card-body">
              <Notice kind="info">{t("companies.participant.noMatches", "No other companies match this search.")}</Notice>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("companies.participant.columns.company", "Company")}</th>
                    <th>{t("companies.participant.columns.cr", "CR")}</th>
                    <th>{t("companies.participant.columns.participant", "Participant")}</th>
                    <th>{t("companies.participant.columns.vatId", "VAT ID")}</th>
                    <th>{t("companies.participant.columns.status", "Status")}</th>
                    <th className="nowrap">{t("companies.participant.columns.lastDocumentIn", "Last document in")}</th>
                  </tr>
                </thead>
                <tbody>
                  {directory.map((row) => (
                    <tr key={row.entityId}>
                      <td>
                        <Link className="link" to={`/companies/${row.entityId}`}>
                          {row.legalName}
                        </Link>
                      </td>
                      <td className="mono nowrap">{row.crNumber}</td>
                      <td className="mono nowrap">
                        <span className="row-tight">
                          {row.participantId}
                          <CopyButton
                            value={row.participantId}
                            label={t("companies.participant.copyParticipantForLabel", "Copy participant ID for {{name}}", {
                              name: row.legalName,
                            })}
                          />
                        </span>
                      </td>
                      <td className="mono nowrap">{row.vatin ?? "—"}</td>
                      <td>
                        <StatusPill status={row.status} />
                      </td>
                      <td className="nowrap">
                        {row.lastInboundAt ? dateTime(row.lastInboundAt) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
